import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import {
  CreateRetentionByPathOptions,
  DocumentRetention,
  ExpiringDocument,
  RetentionAvailableDocumentsResponse,
  RetentionServerDocumentsResponse,
  RetentionStats,
} from '../../../../domain/src/lib/retention/retention.model';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class DocumentRetentionService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly retentionPath = '/api/document-retention';

  private retentionUrl(): string {
    return this.retentionPath;
  }

  listRetentions(status?: string, policyId?: string): Observable<DocumentRetention[]> {
    let params = new HttpParams();
    if (status) params = params.set('status_filter', status);
    if (policyId) params = params.set('policy_id', policyId);
    const tenantId = resolveTenantId(this.serverContext);

    return this.http.get<DocumentRetention[]>(`${this.retentionUrl()}/documents`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }

  getRetention(id: string): Observable<DocumentRetention> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.get<DocumentRetention>(`${this.retentionUrl()}/documents/${id}`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  createRetention(data: Partial<DocumentRetention>): Observable<DocumentRetention> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.post<DocumentRetention>(`${this.retentionUrl()}/documents`, data, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  updateRetention(id: string, data: Partial<DocumentRetention>): Observable<DocumentRetention> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.put<DocumentRetention>(`${this.retentionUrl()}/documents/${id}`, data, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  deleteRetention(id: string): Observable<unknown> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.delete(`${this.retentionUrl()}/documents/${id}`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  suspendRetention(id: string, reason?: string): Observable<DocumentRetention> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.post<DocumentRetention>(
      `${this.retentionUrl()}/documents/${id}/suspend`,
      { reason },
      { headers: tenantHeaders(tenantId), params: withTenantParam(tenantId) },
    );
  }

  resumeRetention(id: string): Observable<DocumentRetention> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.post<DocumentRetention>(
      `${this.retentionUrl()}/documents/${id}/resume`,
      {},
      { headers: tenantHeaders(tenantId), params: withTenantParam(tenantId) },
    );
  }

  getStats(): Observable<RetentionStats> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.get<RetentionStats>(`${this.retentionUrl()}/stats`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  getExpiringDocuments(days?: number): Observable<ExpiringDocument[]> {
    const params = typeof days === 'number' ? new HttpParams().set('days', String(days)) : undefined;
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.get<ExpiringDocument[]>(`${this.retentionUrl()}/documents/expiring-soon`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }

  getAvailableDocuments(search?: string, documentType?: string): Observable<RetentionAvailableDocumentsResponse> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (documentType) params = params.set('document_type', documentType);
    const tenantId = resolveTenantId(this.serverContext);

    return this.http.get<RetentionAvailableDocumentsResponse>(
      `${this.retentionUrl()}/documents/available`,
      {
        headers: tenantHeaders(tenantId),
        params: withTenantParam(tenantId, params),
      },
    );
  }

  getDocumentsFromServer(search?: string, limit?: number): Observable<RetentionServerDocumentsResponse> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (typeof limit === 'number') params = params.set('limit', String(limit));
    const tenantId = resolveTenantId(this.serverContext);

    return this.http.get<RetentionServerDocumentsResponse>(
      `${this.retentionUrl()}/documents/from-server`,
      {
        headers: tenantHeaders(tenantId),
        params: withTenantParam(tenantId, params),
      },
    );
  }

  createRetentionByPath(filePath: string, options?: CreateRetentionByPathOptions): Observable<DocumentRetention> {
    let params = new HttpParams().set('file_path', filePath);
    if (options?.policyId) params = params.set('policy_id', options.policyId);
    if (typeof options?.retentionPeriod === 'number') {
      params = params.set('retention_period', String(options.retentionPeriod));
    }
    if (options?.retentionUnit) params = params.set('retention_unit', options.retentionUnit);
    if (options?.retentionStartDate) params = params.set('retention_start_date', options.retentionStartDate);
    if (options?.dispositionAction) params = params.set('disposition_action', options.dispositionAction);
    if (options?.notes) params = params.set('notes', options.notes);
    const tenantId = resolveTenantId(this.serverContext);

    return this.http.post<DocumentRetention>(`${this.retentionUrl()}/documents/by-path`, null, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }
}
